// src/components/UserManagement.tsx
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useUserContext } from "../contexts/UserContext";
import { toast } from 'react-toastify';
import { useTenantCode } from "../services/hooks/useTenantCode";
import { useBranding } from "../contexts/BrandingContext";
import { switchableBranches } from "../utils/branches";
import { X } from "lucide-react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AdminCapacity {
  maxUsers: number;
  adminCount: number;
  available: number;
}

const UserManagement: React.FC<ModalProps> = ({isOpen, onClose}) => {
  const { getToken } = useAuth(); // Obtener el token para interactuar con la API
  const tenantCode = useTenantCode();
  const { branding } = useBranding();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("user");
  const [branchAccessMode, setBranchAccessMode] = useState<'single' | 'all' | 'subset'>('single');
  const [selectedBranchCodes, setSelectedBranchCodes] = useState<string[]>([]);
  const { users, setUsers } = useUserContext(); // usamos el contexto para actualizar la lista de usuarios

  // M4: real admin-user capacity from the backend (configured limit for this
  // tenant's root family, counted the same way it's enforced) — no hardcoded max.
  const [capacity, setCapacity] = useState<AdminCapacity | null>(null);

  const localAdminCount = users.filter((user) => user.publicMetadata?.role === 'admin').length;
  const usedAdmins = capacity?.adminCount ?? localAdminCount;
  const maxAdmins = capacity?.maxUsers ?? null;
  const availableAdminSlots = capacity?.available ?? null;
  const adminLimitReached = capacity ? capacity.available <= 0 : false;

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${import.meta.env.VITE_API_URL}/users/admin-capacity`, {
          headers: { Authorization: `Bearer ${token}`, 'X-Active-Tenant-Code': tenantCode ?? '' },
        });
        if (res.ok && active) setCapacity(await res.json());
      } catch {
        /* non-fatal: fall back to showing the local count */
      }
    })();
    return () => { active = false; };
  }, [isOpen, tenantCode, getToken, users.length]);

  // P2 — mismo origen que el selector de sucursal de la cabecera. Antes esto
  // añadía el tenant ACTUAL si faltaba, que solo tapaba el hueco estando en la
  // central: desde una sucursal la central seguía sin aparecer, así que no se
  // podía dar acceso `subset` a la central desde esa pantalla.
  const branchOptions = useMemo(() => switchableBranches(branding), [branding]);

  const getDefaultSubsetCodes = () => {
    if (tenantCode && branchOptions.some((branch) => branch.code === tenantCode)) {
      return [tenantCode];
    }

    if (branchOptions.length > 0) {
      return [branchOptions[0].code];
    }

    return [];
  };

  useEffect(() => {
    if (branchAccessMode !== 'subset') {
      return;
    }

    if (selectedBranchCodes.length > 0) {
      return;
    }

    const defaults = getDefaultSubsetCodes();
    if (defaults.length > 0) {
      setSelectedBranchCodes(defaults);
    }
  }, [branchAccessMode, selectedBranchCodes.length, tenantCode, branchOptions]);

  const toggleBranchCode = (code: string) => {
    setSelectedBranchCodes((prev) => (
      prev.includes(code)
        ? prev.filter((item) => item !== code)
        : [...prev, code]
    ));
  };

  if (!isOpen) return null;

  const handleCreateUser = async () => {
    try {
      if (role === 'admin' && adminLimitReached) {
        toast.error(`Límite alcanzado: máximo ${maxAdmins ?? ''} usuarios admin por tenant.`);
        return;
      }

      const token = await getToken(); // Obtener el token de autenticación
      // console.log(token);

      // Llamar a la API de nuestro backend para crear un usuario
      if (role === 'admin' && branchAccessMode === 'subset' && selectedBranchCodes.length === 0) {
        toast.error('Selecciona al menos una sucursal para el acceso por subconjunto.');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/users/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Active-Tenant-Code": tenantCode ?? "",
        },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          role,
          tenantCode,
          branchAccessMode,
          branchCodes: branchAccessMode === 'subset' ? selectedBranchCodes : [],
          branchCode: branchAccessMode === 'single' ? tenantCode : undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error creating user:", errorData);
        
        // Manejar diferentes formatos de error
        if (errorData.errors) {
          throw new Error(errorData.errors.map((err: any) => err.message).join(", "));
        } else {
          throw new Error(errorData.message || "Error creating user.");
        }
      }

      const data = await response.json();

      // Q1 — ya no vuelve un usuario: el de Clerk no existe hasta que aceptan la
      // invitación. Se añade a la lista con la misma forma que `getAllUsers` da a
      // las pendientes, para que el alta no parezca no haber hecho nada.
      setUsers((prevUsers: any[]) => [...prevUsers, {
        id: `invitation_${data?.invitation?.id ?? email}`,
        invitationId: data?.invitation?.id ?? null,
        pendingInvitation: true,
        firstName,
        lastName,
        emailAddresses: [{ emailAddress: email }],
        publicMetadata: { role, tenantCode },
      }]);

      toast.success(`Invitación enviada a ${email}`);
      // Limpiar los campos
      setEmail("");
      setFirstName("");
      setLastName("");
      setBranchAccessMode('single');
      setSelectedBranchCodes([]);
      onClose();
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error((error as Error).message || "Error creating user. Please try again.");
    }
  };

  return (
    <div className="p-4 bg-white rounded shadow-md">
      <div className="mb-4 flex items-start justify-between gap-4">
        <h2 className="text-2xl font-bold">Crear Nuevo Usuario</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Cerrar modal"
        >
          <X size={20} />
        </button>
      </div>
      <div className={`mb-4 rounded-md border px-4 py-3 text-sm ${adminLimitReached ? 'border-red-200 bg-red-50 text-red-700' : 'border-blue-200 bg-blue-50 text-blue-700'}`}>
        <p className="font-semibold">Usuarios administrativos del tenant</p>
        <p>
          Disponibles: {availableAdminSlots ?? '—'} de {maxAdmins ?? '—'}.
          {' '}Actualmente usados: {usedAdmins}.
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleCreateUser();
        }}
      >
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">Correo electrónico:</label>
          <input
            type="email"
            className="border rounded w-full py-2 px-3"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">Nombre:</label>
          <input
            type="text"
            className="border rounded w-full py-2 px-3"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">Apellidos:</label>
          <input
            type="text"
            className="border rounded w-full py-2 px-3"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </div>
        {/* Q1 — ya no hay contraseña. Se manda una invitación y la persona elige
            la suya: antes la escogía el administrador y la conocía. */}
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">Rol:</label>
          <select
            className="border rounded w-full py-2 px-3"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <option value="user">Usuario</option>
            <option value="admin" disabled={adminLimitReached}>Admin</option>
          </select>
          {adminLimitReached && (
            <p className="text-xs text-red-500 mt-1">
              Límite alcanzado: máximo {maxAdmins ?? ''} usuarios admin por tenant.
            </p>
          )}
        </div>
        {role === 'admin' && (
          <div className="mb-4">
            <label className="block text-sm font-bold mb-2">Acceso a sucursales:</label>
            <select
              className="border rounded w-full py-2 px-3"
              value={branchAccessMode}
              onChange={(e) => {
                const nextMode = e.target.value as 'single' | 'all' | 'subset';
                setBranchAccessMode(nextMode);
                if (nextMode === 'subset') {
                  setSelectedBranchCodes(getDefaultSubsetCodes());
                } else {
                  setSelectedBranchCodes([]);
                }
              }}
            >
              <option value="single">Solo la sucursal actual</option>
              <option value="all">Todas las sucursales del tenant</option>
              <option value="subset">Subconjunto de sucursales</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Este valor se guardará en el usuario para decidir si ve un único punto de acceso o un selector de sucursales.
            </p>

            {branchAccessMode === 'subset' && (
              <div className="mt-3 rounded border border-gray-200 p-3">
                <p className="text-sm font-semibold text-gray-700 mb-2">Sucursales permitidas</p>
                {branchOptions.length === 0 ? (
                  <p className="text-xs text-gray-500">No hay sucursales activas disponibles para seleccionar.</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {branchOptions.map((branch) => (
                      <label key={branch.code} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={selectedBranchCodes.includes(branch.code)}
                          onChange={() => toggleBranchCode(branch.code)}
                        />
                        <span>{branch.name}</span>
                        <span className="text-xs text-gray-400">({branch.code})</span>
                        {branch.isRoot && (
                          <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                            Central
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="submit"
            disabled={role === 'admin' && adminLimitReached}
            title={role === 'admin' && adminLimitReached ? `Límite alcanzado: máximo ${maxAdmins ?? ''} usuarios admin por tenant` : undefined}
            className={`text-white font-bold py-2 px-4 rounded ${role === 'admin' && adminLimitReached ? 'bg-gray-400 cursor-not-allowed' : 'bg-brand-primary hover:brightness-90'}`}
          >
            {role === 'admin' && adminLimitReached ? `Límite admins (${maxAdmins ?? ''})` : 'Guardar'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="border border-gray-300 text-gray-700 hover:bg-gray-50 font-bold py-2 px-4 rounded"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserManagement;
